'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';

interface CurrencyContextType {
  currency: string;
  defaultCurrency: string;
  rates: Record<string, number>;
  setCurrency: (currency: string) => void;
  convertPrice: (priceInAED: any) => number;
  formatPrice: (priceInAED: any, priceLabel?: string | null) => string;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

const defaultRates: Record<string, number> = {
  USD: 3.6725,
  EUR: 3.95,
  RUB: 0.04,
  AED: 1.0,
};

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currency, setCurrencyState] = useState('AED');
  const [defaultCurrency, setDefaultCurrency] = useState('AED');
  const [rates, setRates] = useState<Record<string, number>>(defaultRates);

  useEffect(() => {
    // Load saved currency choice from localStorage if present
    const savedCurrency = localStorage.getItem('plotae_currency');
    if (savedCurrency) {
      setCurrencyState(savedCurrency);
    }

    // Fetch site-wide baseline settings
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch('/api/settings');
      if (res.ok) {
        const data = await res.json();
        if (data.default_currency) {
          setDefaultCurrency(data.default_currency);
          // Set to default currency only if user has not already selected a specific currency
          if (!localStorage.getItem('plotae_currency')) {
            setCurrencyState(data.default_currency);
          }
        }
        if (data.currency_rates) {
          setRates(data.currency_rates);
        }
      }
    } catch (e) {
      console.error('Error loading currency settings:', e);
    }
  };

  const setCurrency = (newCurrency: string) => {
    setCurrencyState(newCurrency);
    localStorage.setItem('plotae_currency', newCurrency);
  };

  const convertPrice = (priceInAED: any): number => {
    if (priceInAED === null || priceInAED === undefined) return 0;

    // Clean price string if passed (e.g. 'AED 60,000,000' -> 60000000)
    let numericPrice = 0;
    if (typeof priceInAED === 'string') {
      const clean = priceInAED.replace(/[^0-9.]/g, '');
      numericPrice = parseFloat(clean) || 0;
    } else {
      numericPrice = Number(priceInAED) || 0;
    }

    const rate = rates[currency] || 1.0;
    return numericPrice / rate;
  };

  const formatPrice = (priceInAED: any, priceLabel?: string | null): string => {
    if (priceInAED === null || priceInAED === undefined || priceInAED === '') return '';

    // If it's a completely custom price label and has no numeric price, return as is
    if (typeof priceInAED === 'string') {
      const clean = priceInAED.replace(/[^0-9.]/g, '');
      if (clean === '') {
        return priceInAED;
      }
    }

    const converted = convertPrice(priceInAED);
    const formattedNum = new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0,
    }).format(converted);

    // Dynamic Currency Prepend/Append symbols
    let symbolPrefix = '';
    const symbolSuffix = '';

    switch (currency) {
      case 'USD':
        symbolPrefix = '$';
        break;
      case 'EUR':
        symbolPrefix = '€';
        break;
      case 'RUB':
        symbolPrefix = '₽';
        break;
      default:
        symbolPrefix = 'AED ';
        break;
    }

    const suffixStr = priceLabel ? ` ${priceLabel}` : '';
    return `${symbolPrefix}${formattedNum}${symbolSuffix}${suffixStr}`;
  };

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        defaultCurrency,
        rates,
        setCurrency,
        convertPrice,
        formatPrice,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
